<template>
  <v-card class="pa-4" tile min-height="200">
    <!-- Header Section -->
    <div class="d-flex align-center mb-4">
      <v-avatar :color="selected_avatar" size="40" class="mr-3 flex-shrink-0">
        <span class="white--text caption">{{ item.government }}</span>
      </v-avatar>
      <div class="flex-grow-1 d-flex align-center">
        <div class="publisher-link-wrapper">
          <router-link
            text
            style="text-decoration: none !important; color:grey !important;"
            :to="{
              name: 'entity',
              params: { id: item.publisher_id }
            }"
            class="body-2 font-weight-medium publisher-link"
          >
            {{ item.publisher }}
          </router-link>
        </div>
      </div>
      <div class="d-flex align-center desktop-nav">
        <span class="desktop-nav" style="text-decoration: none; color:grey !important;">
          <router-link
            text
            style="text-decoration: none !important;"
            :to="{
              name: 'supplier',
              params: { id: item.supplier_id }
            }"
            >{{ item.supplier }}</router-link
          >
        </span>
        <v-divider
          v-if="item.supplier"
          vertical
          class="my-1 mx-3 desktop-nav"
        ></v-divider>
        <span class="desktop-nav body-2 font-weight-medium" style="color:grey !important;">{{
          formattedCurrency(item.value)
        }}</span>
      </div>
    </div>

    <v-row>
      <v-col cols="12">
        <!-- Title Section - Most Prominent -->
        <h2 class="text-h6 font-weight-bold mb-4 pa-0">
          {{ item.title }}
        </h2>

        <!-- Primary Information Group -->
        <div class="mb-4">
          <div class="d-flex align-center mb-2">
            <span class="subtitle-2 font-weight-bold card-data"
              >Public Entity</span
            >
              <span class="body-2 font-weight-normal"
              ><router-link
                text
                style="text-decoration: none !important;"
                :to="{
                  name: 'entity',
                  params: { id: item.publisher_id }
                }"
              >
                <span class="body-2 font-weight-normal">{{
                  item.publisher
                }}</span>
              </router-link></span
            >
          </div>

          <div
            v-if="item.publisher_division"
            class="d-flex align-center mb-2"
          >
            <span class="subtitle-2 font-weight-bold card-data">Division</span>
            <span class="body-2 font-weight-normal">{{
              item.publisher_division
            }}</span>
          </div>

          <div v-if="item.value" class="d-flex align-center mb-2">
            <span class="subtitle-2 font-weight-bold card-data"
              >Contract Value</span
            >
            <span class="body-2 font-weight-medium">{{
              formattedCurrency(item.value)
            }}</span>
          </div>
        </div>

        <!-- Supplier Information Group -->
        <div v-if="item.supplier || item.supplier_company_number" class="mb-4">
          <div v-if="item.supplier" class="d-flex align-center mb-2">
            <span class="subtitle-2 font-weight-bold card-data">Supplier</span>
            <span class="body-2 font-weight-normal"
              ><router-link
                text
                style="text-decoration: none !important;"
                :to="{
                  name: 'supplier',
                  params: { id: item.supplier_id }
                }"
                >{{ item.supplier }}</router-link
              ></span
            >
          </div>
          <div
            v-if="item.supplier_company_number"
            class="d-flex align-center mb-2"
          >
            <span class="subtitle-2 font-weight-bold card-data"
              >Supplier ABN</span
            >
            <span class="body-2 font-weight-normal">{{
              item.supplier_company_number
            }}</span>
          </div>
        </div>

        <!-- Contract Dates Group -->
        <div v-if="item.contract_start || item.expiry_date" class="mb-4">
          <div v-if="item.contract_start" class="d-flex align-center mb-2">
            <span class="subtitle-2 font-weight-bold card-data"
              >Contract Start</span
            >
            <span class="body-2 font-weight-normal">{{
              formatDateLong(item.contract_start)
            }}</span>
          </div>

          <div v-if="item.expiry_date" class="d-flex align-center mb-2">
            <span class="subtitle-2 font-weight-bold card-data">Expiry</span>
            <span class="body-2 font-weight-normal">{{
              formatDateLong(item.expiry_date)
            }}</span>
          </div>
        </div>

        <!-- Metadata Group -->
        <div class="mb-4">
          <div class="d-flex align-center mb-2">
            <span class="subtitle-2 font-weight-bold card-data">Identifier</span>
            <span class="body-2 font-weight-normal">{{ item.recid }}</span>
          </div>
          <div class="d-flex align-center mb-2">
            <span class="subtitle-2 font-weight-bold card-data">Category</span>
            <span class="body-2 font-weight-normal"
              >{{ item.category }}<span v-if="item.category_code"> ({{ item.category_code }})</span></span
            >
          </div>
        </div>

        <!-- Description Section -->
        <div v-if="item.description" class="mb-4">
          <div class="subtitle-2 font-weight-bold card-data mb-2">
            Description
          </div>
          <div class="body-2 font-weight-normal" style="line-height: 1.6;">
            {{ item.description }}
          </div>
        </div>

        <!-- Footer Actions -->
        <v-divider class="mt-4 mb-3"></v-divider>

        <v-card-actions class="px-0">
          <div class="d-flex align-center">
            <v-icon v-if="item.contract_end || item.closing_date" color="grey" size="18" class="mr-2">
              mdi-clock
            </v-icon>
            <span
              v-if="item.contract_end || item.closing_date"
              class="subtitle-2 font-weight-normal"
              style="color:grey"
              >{{
                item.contract_end
                  ? formatDate(item.contract_end)
                  : formatDate(item.closing_date)
              }}</span
            >
          </div>

          <v-spacer></v-spacer>

          <v-tooltip top dark color="#000000" style="opacity: 0.7;">
            <template v-slot:activator="{ on }">
              <v-btn
                small
                color="#4EB888"
                target="_blank"
                light
                icon
                :to="{ name: 'tender', params: { id: item.id } }"
                v-on="on"
                class="mr-1"
              >
                <v-icon>mdi-open-in-new</v-icon>
              </v-btn>
            </template>
            <span>Open full record</span>
          </v-tooltip>

          <v-tooltip top dark color="#000000" style="opacity: 0.7;">
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                large
                icon
                color="#4EB888"
                light
                :href="item.url"
                target="_blank"
                :disabled="disableIcon(item.url)"
                class="mr-1"
              >
                <v-icon>mdi-web</v-icon>
              </v-btn>
            </template>
            <span>Go to Source</span>
          </v-tooltip>

          <v-tooltip top dark color="#000000" style="opacity: 0.7;">
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                large
                color="#FF6A9C"
                light
                icon
                @click="hideProfile()"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
            <span>Close</span>
          </v-tooltip>
        </v-card-actions>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { bus } from "../main.js";
import moment from "moment";
import { getCurrencySymbol } from "./Utils";

export default {
  props: {
    item: Object,
    selected_avatar: String
  },
  data() {
    return {
      show: null
    };
  },
  methods: {
    hideProfile() {
      this.show = false;
      this.$emit("hideProfile", this.show);
    },
    formatDate(date_value) {
      return moment(date_value).format("MMMM YYYY");
    },

    getLists(tender_id) {
      bus.$emit("get-lists", tender_id);
    },

    formatDateLong(date_value) {
      return moment(date_value).format("DD MMMM YYYY");
    },
    formattedCurrency: function(amount) {
      ////////////console.log("AMOUNT: " + amount);
      if (!isNaN(amount)) {
        return (
          getCurrencySymbol() +
          Math.round(amount)
            .toString()
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        );
      } //else {
      //return null;
      //}
    },
    getStatusColour(item) {
      if (item.current) {
        return "#b3bc4b";
      } else {
        return "#E0E0E0";
      }
    },

    disableIcon(url) {
      if (!url) {
        return true;
      } else {
        return false;
      }
    }
  }
};
</script>

<style scoped>
::v-deep .v-expansion-panel-content__wrap {
  padding-left: 0px;
}

.v-card__text,
.v-card__title {
  word-break: normal; /* maybe !important  */
}

.tender {
  border-left: 5px solid #ff4081 !important;
}

.card-data {
  width: 150px !important;
  flex-shrink: 0;
}

/* Improved spacing and hierarchy */
h2 {
  line-height: 1.3;
  letter-spacing: -0.01em;
}

/* Ensure publisher text aligns with avatar and wraps to 2 lines */
.publisher-link-wrapper {
  max-width: 250px;
  min-width: 150px;
  display: flex;
  align-items: center;
}

.publisher-link {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  word-break: break-word;
}

table {
  border-collapse: collapse;
}

th {
  border-bottom: 1px solid #535353;
}

/* .tr:hover {
  transition: opacity 0.4s ease-in-out;
} */

tbody tr:hover {
  background: #535353;

  -webkit-transition: background-color 0.4s; /* For Safari 3.1 to 6.0 */
  transition: background-color 0.4s;
}

.v-select--dense .v-input__control {
  height: 20px !important;
}

.state-line {
  width: 100%;
  text-align: right;
  border-bottom: 1px solid #4eb888;
  line-height: 0.1em;
  margin: 10px 0px 20px;
}
.state-line-span {
  padding: 0 10px;
}

.state {
  margin: 20px;
  overflow: hidden;
  color: gray;
  text-align: right;
  line-height: 1.6em;
}

.state:before {
  display: block;
  float: left;
  margin-top: 0.8em; /* half the line-height */
  border-top: 2px solid silver;
  width: 95%;
  content: "";
}
</style>
